import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, LogOut, ArrowLeft, Inbox } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-tinta border border-inkline text-creme placeholder:text-muteddark/60 font-inter text-sm px-4 h-12 focus:border-lima focus:outline-none transition-colors duration-150";

const formatError = (detail) => {
  if (detail == null) return "Algo deu errado. Tente novamente.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
};

export default function Admin() {
  const [user, setUser] = useState(null); // null = verificando, false = deslogado
  const [leads, setLeads] = useState([]);
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const loadLeads = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/leads`, { withCredentials: true });
      setLeads(data.leads || []);
    } catch {
      toast.error("Não foi possível carregar os leads.");
    }
  };

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => {
        setUser(r.data);
        loadLeads();
      })
      .catch(() => setUser(false));
  }, []);

  const login = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, form, { withCredentials: true });
      setUser(data);
      toast.success("Login realizado.");
      loadLeads();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    setUser(false);
    setLeads([]);
  };

  const exportCsv = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/leads.csv`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "leads-dealflow.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Não foi possível exportar.");
    }
  };

  return (
    <div data-testid="admin-page" className="bg-tinta min-h-screen text-creme">
      <header className="border-b border-inkline">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <a href="/" aria-label="DealFlow Lab — voltar à página inicial">
            <img src="/assets/logo-horizontal.webp" alt="DealFlow Lab" className="h-9 w-auto" />
          </a>
          <div className="flex items-center gap-3">
            {user && (
              <button
                data-testid="admin-logout-button"
                onClick={logout}
                className="inline-flex items-center gap-2 border border-inkline text-creme/80 font-sora text-xs font-bold px-4 h-10 hover:border-lima hover:text-lima transition-colors duration-150"
              >
                <LogOut className="w-4 h-4" /> Sair
              </button>
            )}
            <a
              href="/"
              className="inline-flex items-center gap-2 font-sora text-xs font-bold text-creme/80 hover:text-lima transition-colors duration-150"
            >
              <ArrowLeft className="w-4 h-4" /> Site
            </a>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-14 md:py-20">
        {user === null && (
          <p className="text-muteddark text-sm">Verificando sessão...</p>
        )}

        {user === false && (
          <form
            data-testid="admin-login-form"
            onSubmit={login}
            className="max-w-md mx-auto border border-inkline p-8 md:p-10"
          >
            <p className="label-caps text-lima mb-4">Área restrita</p>
            <h1 className="headline text-2xl sm:text-3xl">Painel de leads</h1>
            <div className="mt-8 space-y-4">
              <div>
                <label htmlFor="admin-email" className="label-caps text-muteddark !text-[10px] block mb-2">E-mail</label>
                <input
                  id="admin-email"
                  data-testid="admin-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="admin@dealflowlab.com.br"
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="admin-password" className="label-caps text-muteddark !text-[10px] block mb-2">Senha</label>
                <input
                  id="admin-password"
                  data-testid="admin-password"
                  type="password"
                  required
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="••••••••"
                  className={inputCls}
                />
              </div>
              <button
                data-testid="admin-login-button"
                type="submit"
                disabled={loading}
                className="w-full bg-lima text-tinta font-sora font-bold text-sm tracking-[-0.02em] py-4 hover:bg-[#d4ff66] disabled:opacity-60 transition-colors duration-150"
              >
                {loading ? "Entrando..." : "Entrar"}
              </button>
            </div>
          </form>
        )}

        {user && (
          <>
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
              <div>
                <p className="label-caps text-lima mb-4">Área restrita</p>
                <h1 data-testid="admin-title" className="headline text-3xl sm:text-4xl">
                  Pedidos de diagnóstico
                </h1>
                <p data-testid="admin-count" className="mt-3 text-muteddark text-sm">
                  {leads.length} {leads.length === 1 ? "lead recebido" : "leads recebidos"}
                </p>
              </div>
              <button
                data-testid="admin-export-button"
                onClick={exportCsv}
                className="inline-flex items-center justify-center gap-2 bg-lima text-tinta font-sora font-bold text-xs tracking-[-0.01em] px-6 h-11 hover:bg-[#d4ff66] transition-colors duration-150"
              >
                <Download className="w-4 h-4" /> Exportar CSV
              </button>
            </div>

            {leads.length === 0 ? (
              <div className="mt-14 border border-inkline p-12 text-center">
                <Inbox className="w-8 h-8 text-lima mx-auto" />
                <p className="mt-4 text-muteddark text-sm">Nenhum lead por aqui ainda.</p>
              </div>
            ) : (
              <div className="mt-10 border border-inkline overflow-x-auto">
                <table data-testid="admin-leads-table" className="w-full text-sm min-w-[820px]">
                  <thead>
                    <tr className="border-b border-inkline text-left">
                      {["Data", "Nome", "Empresa", "WhatsApp", "E-mail", "Ticket médio", "Mensagem"].map((h) => (
                        <th key={h} className="label-caps !text-[10px] text-muteddark font-sora px-5 py-4">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map((lead) => (
                      <tr key={lead.id} data-testid={`admin-lead-row-${lead.id}`} className="border-b border-inkline last:border-0 hover:bg-inksoft transition-colors duration-150">
                        <td className="px-5 py-4 text-muteddark whitespace-nowrap">
                          {lead.created_at ? new Date(lead.created_at).toLocaleString("pt-BR") : "—"}
                        </td>
                        <td className="px-5 py-4 font-semibold text-creme">{lead.nome}</td>
                        <td className="px-5 py-4 text-muteddark">{lead.empresa}</td>
                        <td className="px-5 py-4 text-muteddark whitespace-nowrap">{lead.whatsapp}</td>
                        <td className="px-5 py-4 text-muteddark">{lead.email}</td>
                        <td className="px-5 py-4 text-muteddark whitespace-nowrap">{lead.ticket_medio || "—"}</td>
                        <td className="px-5 py-4 text-muteddark max-w-[260px] truncate" title={lead.mensagem}>
                          {lead.mensagem || "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
